import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Loader2, ExternalLink, Phone } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useAppointment } from '@/hooks/useAppointments'
import { formatCurrency } from '@/lib/utils'

interface AppointmentSheetProps {
  appointmentId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STATUS_LABEL: Record<string, string> = {
  turn_up: 'Turn Up',
  did_not_turn_up: 'No-flip (default DNTU)',
  cancelled: 'Cancelled',
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-text-muted mb-0.5">{label}</div>
      <div className="text-[13px] text-text-dark">{value || <span className="text-text-muted italic">—</span>}</div>
    </div>
  )
}

export function AppointmentSheet({ appointmentId, open, onOpenChange }: AppointmentSheetProps) {
  const navigate = useNavigate()
  const { data, isLoading } = useAppointment(appointmentId)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        )}

        {data && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {data.data.isTask && <Phone className="w-4 h-4 text-text-muted" />}
                {data.data.patientLinkedDisplayName ?? data.data.patientLinkedName ?? data.data.patientName ?? 'Task placeholder'}
              </SheetTitle>
              <SheetDescription>
                {new Date(data.data.appointmentAt).toLocaleString('en-MY', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'Asia/Kuala_Lumpur',
                })}
                {' · '}
                {data.data.branchName}
              </SheetDescription>
            </SheetHeader>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <Field label="Status" value={
                <Badge>{STATUS_LABEL[data.data.status] ?? data.data.status}</Badge>
              } />
              <Field label="Patient Type" value={data.data.patientType} />
              <Field label="Doctor" value={data.data.doctor} />
              <Field label="Created By" value={data.data.createdBy} />
              <Field label="PNO" value={data.data.pno} />
              <Field label="NRIC" value={data.data.patientNric ? <span className="font-mono">{data.data.patientNric}</span> : null} />
              <Field label="Time In" value={data.data.timeIn} />
              <Field label="Time Out" value={data.data.timeOut} />
            </div>

            <div className="mt-6 space-y-3">
              <Field label="Visit Reason" value={
                data.data.visitReasonCategory ? (
                  <div>
                    <Badge variant="outline">{data.data.visitReasonCategory}</Badge>
                    {data.data.visitReasonDetail && <div className="text-xs text-muted-foreground mt-1">{data.data.visitReasonDetail}</div>}
                  </div>
                ) : null
              } />

              {data.data.cancelComment && (
                <Field label="Cancel Comment" value={
                  <span className="italic">{data.data.cancelComment}</span>
                } />
              )}

              {data.data.invoiceNo && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted mb-0.5">Invoice</div>
                  <div className="flex items-center justify-between bg-background/50 rounded-lg p-3 border border-border">
                    <div>
                      <div className="font-mono text-[13px]">{data.data.invoiceNo}</div>
                      {data.data.invoiceAmount && (
                        <div className="text-info font-semibold">{formatCurrency(parseFloat(data.data.invoiceAmount))}</div>
                      )}
                    </div>
                    {data.linkedInvoice && (
                      <button
                        onClick={() => navigate({ to: '/invoices/$id', params: { id: String(data.linkedInvoice!.id) } })}
                        className="text-info hover:underline flex items-center gap-1 text-[12px]"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {data.data.patientId && (
                <button
                  onClick={() => navigate({ to: '/patients/$id', params: { id: String(data.data.patientId) } })}
                  className="w-full text-center bg-info/10 text-info hover:bg-info/20 rounded-lg py-2 text-[13px] font-medium transition-colors flex items-center justify-center gap-1"
                >
                  View patient profile <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}

              {data.data.type && (
                <Field label="Type (Avixo internal)" value={<span className="font-mono text-xs">{data.data.type}</span>} />
              )}

              <div className="text-[10px] text-text-muted text-right pt-2 border-t border-border">
                Last scraped: {new Date(data.data.scrapedAt).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' })}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
